import dynamic from "next/dynamic"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { i18n } from "next-i18next"
import { footerUrl, headerUrl, SMART_TOILETS_MODEL_JSON } from "@/constants"
import {
  getAemServerAxiosInstance,
  getAemServerModelJsonAxiosInstance,
} from "@/services/server/aem.server"
import { filterBackEndJson, getDataLayerObj } from "@/utils/helper"
import { useEffect } from "react"
import usePageAuth from "@/hooks/usePageAuth"
import _isEmpty from "lodash/isEmpty"
import PropTypes from "prop-types"

const AemGrid = dynamic(() => import("@/components/AemGrid"))
const GlobalPageHeader = dynamic(() =>
  import("@/components/Shared/GlobalPage/GlobalPageHeader")
)
const GlobalPageFooter = dynamic(() =>
  import("@/components/Shared/GlobalPage/GlobalPageFooter")
)

/**
 *
 * @return {JSX.Element}
 */
const SmartToilets = ({
  tmpData: data,
  header,
  footer,
  analyticsData = {},
  globalScript = null,
  footerScript = null,
}) => {
  usePageAuth()

  useEffect(() => {
    getDataLayerObj(analyticsData)
  }, [analyticsData])

  return (
    <>
      {header ? (
        <div className="nav-light-theme nav-transparent-light-theme">
          <GlobalPageHeader
            globalScripting={globalScript}
            themeType={data?.appliedCssClassNames}
            header={header}
            data={data}
            title={data?.title}
            description={data?.description}
          />
        </div>
      ) : null}
      <div className="min-vh-100" style={{ background: "transparent" }}>
        <AemGrid data={data} count={0} />
      </div>
      {footer ? (
        <GlobalPageFooter footer={footer} footerScripting={footerScript} />
      ) : null}
    </>
  )
}

/**
 *
 * @return {Object}
 */
export async function getServerSideProps({ locale }) {
  const serverAemApi = getAemServerAxiosInstance()
  const serverAemModelJsonApi = getAemServerModelJsonAxiosInstance()
  await i18n?.reloadResources()

  const { data = {} } = await serverAemModelJsonApi({
    url: `/${locale}${SMART_TOILETS_MODEL_JSON}`,
    method: "GET",
  })

  const {
    headerExpFrg = headerUrl,
    footerExpFrg = footerUrl,
    pageComponentProperties = {},
    data: analyticsData = {},
    topHeadScript = null,
    globalScript = null,
    footerScript = null,
  } = data

  let tmpData = !_isEmpty(pageComponentProperties)
    ? pageComponentProperties
    : data

  const { data: headerData } = await serverAemApi({
    url: `${headerExpFrg}.model.json`,
    method: "GET",
  })

  const { data: footerData } = await serverAemApi({
    url: `${footerExpFrg}.model.json`,
    method: "GET",
  })

  tmpData = filterBackEndJson(tmpData)
  let header
  if (headerData) header = filterBackEndJson(headerData)
  let footer
  if (footerData) footer = filterBackEndJson(footerData)

  // Pass data to the page via props
  return {
    props: {
      header,
      footer,
      analyticsData,
      tmpData,
      topHeadScript,
      globalScript,
      footerScript,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  }
}

SmartToilets.propTypes = {
  tmpData: PropTypes.object.isRequired,
  header: PropTypes.object.isRequired,
  footer: PropTypes.object.isRequired,
  analyticsData: PropTypes.object.isRequired,
  globalScript: PropTypes.string,
  footerScript: PropTypes.string,
}

export default SmartToilets
